import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { getSchedule, updateSchedule, getCalendarExceptions, createCalendarException, deleteCalendarException } from '../lib/api';
import { toast } from 'sonner';
import { Clock, CalendarDays, Plus, Trash2, Save, Copy } from 'lucide-react';
import { es } from 'date-fns/locale';
import {
  datePickerToClinicYmd,
  formatAppointmentDateOnlyInClinic,
  ymdToDatePickerDate,
} from '../lib/clinicTime';

const DAYS_OF_WEEK = [
  { id: 1, name: 'Lunes' },
  { id: 2, name: 'Martes' },
  { id: 3, name: 'Miércoles' },
  { id: 4, name: 'Jueves' },
  { id: 5, name: 'Viernes' },
  { id: 6, name: 'Sábado' },
  { id: 7, name: 'Domingo' },
];

const defaultDayHours = () => ({
  opening_time: '09:00',
  closing_time: '17:00',
  lunch_start: '14:00',
  lunch_end: '15:00',
});

/** Mongo/API a veces devuelve "1" en lugar de 1; sin esto los inputs quedan disabled. */
function normalizeWorkingDays(raw) {
  if (!Array.isArray(raw)) return [1, 2, 3, 4, 5];
  const days = raw
    .map((d) => Number(d))
    .filter((d) => Number.isFinite(d) && d >= 1 && d <= 7);
  return days.length ? [...new Set(days)].sort((a, b) => a - b) : [1, 2, 3, 4, 5];
}

function scheduleSaveErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  }
  return 'Error al guardar horarios';
}

function normalizeScheduleFromApi(data) {
  const base = {
    opening_time: data.opening_time || '09:00',
    closing_time: data.closing_time || '17:00',
    lunch_start: data.lunch_start || '14:00',
    lunch_end: data.lunch_end || '15:00',
    working_days: normalizeWorkingDays(data.working_days),
    appointment_duration: data.appointment_duration != null ? Number(data.appointment_duration) : 30,
    urgent_opening_time: data.urgent_opening_time || '19:30',
    urgent_closing_time: data.urgent_closing_time || '20:30',
    urgent_appointment_duration:
      data.urgent_appointment_duration != null ? Number(data.urgent_appointment_duration) : 30,
    per_day: {},
  };
  const fallbackDay = () => ({
    opening_time: base.opening_time,
    closing_time: base.closing_time,
    lunch_start: base.lunch_start,
    lunch_end: base.lunch_end,
  });
  for (let d = 1; d <= 7; d++) {
    const k = String(d);
    const incoming = data.per_day && data.per_day[k];
    base.per_day[k] = incoming
      ? {
          opening_time: incoming.opening_time || base.opening_time,
          closing_time: incoming.closing_time || base.closing_time,
          lunch_start: incoming.lunch_start || base.lunch_start,
          lunch_end: incoming.lunch_end || base.lunch_end,
        }
      : fallbackDay();
  }
  return base;
}

export default function AdminSchedule() {
  const [schedule, setSchedule] = useState(() => {
    const empty = defaultDayHours();
    return {
      ...empty,
      working_days: [1, 2, 3, 4, 5],
      appointment_duration: 30,
      urgent_opening_time: '19:30',
      urgent_closing_time: '20:30',
      urgent_appointment_duration: 30,
      per_day: Object.fromEntries([1, 2, 3, 4, 5, 6, 7].map((d) => [String(d), { ...empty }])),
    };
  });
  const [template, setTemplate] = useState(defaultDayHours());
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newException, setNewException] = useState({
    date: null,
    reason: '',
    allDay: true,
    closedStart: '14:00',
    closedEnd: '17:00',
  });

  const formatExceptionScope = (exc) => {
    const isAllDay = exc.all_day !== false && !String(exc.closed_start || '').trim();
    if (isAllDay) return 'Todo el día';
    const a = String(exc.closed_start || '').slice(0, 5);
    const b = String(exc.closed_end || '').slice(0, 5);
    if (a && b) return `${a} – ${b}`;
    return 'Horario parcial';
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [scheduleData, exceptionsData] = await Promise.all([
        getSchedule(),
        getCalendarExceptions(),
      ]);
      const normalized = normalizeScheduleFromApi(scheduleData);
      setSchedule(normalized);
      setTemplate({
        opening_time: normalized.per_day['1'].opening_time,
        closing_time: normalized.per_day['1'].closing_time,
        lunch_start: normalized.per_day['1'].lunch_start,
        lunch_end: normalized.per_day['1'].lunch_end,
      });
      setExceptions(exceptionsData);
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handlePerDayChange = (dayId, field, value) => {
    const k = String(dayId);
    setSchedule((prev) => ({
      ...prev,
      per_day: {
        ...prev.per_day,
        [k]: { ...prev.per_day[k], [field]: value },
      },
    }));
  };

  const handleDayToggle = (dayId) => {
    const id = Number(dayId);
    setSchedule((prev) => {
      const current = normalizeWorkingDays(prev.working_days);
      const days = current.includes(id)
        ? current.filter((d) => d !== id)
        : [...current, id].sort((a, b) => a - b);
      return { ...prev, working_days: days };
    });
  };

  const applyTemplateToAllDays = () => {
    setSchedule((prev) => {
      const nextPer = { ...prev.per_day };
      for (let d = 1; d <= 7; d++) {
        nextPer[String(d)] = { ...template };
      }
      return { ...prev, per_day: nextPer };
    });
    toast.success('Horario de plantilla aplicado a todos los días');
  };

  const handleSaveSchedule = async () => {
    setSaving(true);
    try {
      const mon = schedule.per_day['1'];
      const payload = {
        opening_time: mon.opening_time,
        closing_time: mon.closing_time,
        lunch_start: mon.lunch_start,
        lunch_end: mon.lunch_end,
        working_days: normalizeWorkingDays(schedule.working_days),
        appointment_duration: schedule.appointment_duration,
        urgent_opening_time: schedule.urgent_opening_time,
        urgent_closing_time: schedule.urgent_closing_time,
        urgent_appointment_duration: schedule.urgent_appointment_duration,
        per_day: schedule.per_day,
      };
      await updateSchedule(payload);
      toast.success('Horarios actualizados');
    } catch (error) {
      toast.error(scheduleSaveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleAddException = async () => {
    if (!newException.date || !newException.reason.trim()) {
      toast.error('Completa fecha y motivo');
      return;
    }
    if (!newException.allDay) {
      if (!newException.closedStart || !newException.closedEnd) {
        toast.error('Indica hora de inicio y fin del cierre');
        return;
      }
      if (newException.closedStart >= newException.closedEnd) {
        toast.error('La hora de fin debe ser posterior a la de inicio');
        return;
      }
    }
    try {
      const payload = {
        date: datePickerToClinicYmd(newException.date),
        reason: newException.reason.trim(),
        is_closed: true,
        all_day: newException.allDay,
      };
      if (!newException.allDay) {
        payload.closed_start = newException.closedStart;
        payload.closed_end = newException.closedEnd;
      }
      await createCalendarException(payload);
      toast.success('Excepción agregada');
      setDialogOpen(false);
      setNewException({
        date: null,
        reason: '',
        allDay: true,
        closedStart: '14:00',
        closedEnd: '17:00',
      });
      loadData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al agregar excepción');
    }
  };

  const handleDeleteException = async (id) => {
    if (!window.confirm('¿Eliminar esta excepción?')) return;
    try {
      await deleteCalendarException(id);
      toast.success('Excepción eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar excepción');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando configuración...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-schedule">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground">Horarios</h1>
        <p className="text-muted-foreground">
          Configura apertura, cierre y receso de comida por cada día. Marca los días en que atiende el consultorio.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-border lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Horarios de atención por día
            </CardTitle>
            <p className="text-sm text-muted-foreground pt-1">
              Plantilla rápida: edita los cuatro horarios y pulsa &quot;Aplicar a todos&quot; para copiarlos a lunes a domingo; luego ajusta cada día si hace falta.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-wrap items-end gap-4 p-4 bg-sage-50 rounded-lg border border-border/60">
              <div>
                <Label htmlFor="tpl_open" className="text-xs">Apertura</Label>
                <Input
                  id="tpl_open"
                  type="time"
                  value={template.opening_time}
                  onChange={(e) => setTemplate((t) => ({ ...t, opening_time: e.target.value }))}
                  className="h-10 mt-1 w-[140px]"
                />
              </div>
              <div>
                <Label htmlFor="tpl_close" className="text-xs">Cierre</Label>
                <Input
                  id="tpl_close"
                  type="time"
                  value={template.closing_time}
                  onChange={(e) => setTemplate((t) => ({ ...t, closing_time: e.target.value }))}
                  className="h-10 mt-1 w-[140px]"
                />
              </div>
              <div>
                <Label htmlFor="tpl_ls" className="text-xs">Comida inicio</Label>
                <Input
                  id="tpl_ls"
                  type="time"
                  value={template.lunch_start}
                  onChange={(e) => setTemplate((t) => ({ ...t, lunch_start: e.target.value }))}
                  className="h-10 mt-1 w-[140px]"
                />
              </div>
              <div>
                <Label htmlFor="tpl_le" className="text-xs">Comida fin</Label>
                <Input
                  id="tpl_le"
                  type="time"
                  value={template.lunch_end}
                  onChange={(e) => setTemplate((t) => ({ ...t, lunch_end: e.target.value }))}
                  className="h-10 mt-1 w-[140px]"
                />
              </div>
              <Button type="button" variant="secondary" className="rounded-full gap-1" onClick={applyTemplateToAllDays}>
                <Copy className="w-4 h-4" />
                Aplicar a todos
              </Button>
            </div>

            <div>
              <Label htmlFor="appointment_duration">Duración de cita (minutos)</Label>
              <Input
                id="appointment_duration"
                type="number"
                min="15"
                step="15"
                value={schedule.appointment_duration}
                onChange={(e) =>
                  setSchedule((prev) => ({ ...prev, appointment_duration: parseInt(e.target.value, 10) || 30 }))
                }
                className="h-11 mt-1 w-32"
                data-testid="input-duration"
              />
            </div>

            <div className="p-4 rounded-lg border border-orange-200/80 bg-orange-50/40 dark:bg-orange-950/20 space-y-4">
              <div>
                <h3 className="font-semibold text-foreground">Citas urgentes (WhatsApp)</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Ventana horaria global para la opción 5 del menú. Respeta los mismos días activos de la tabla inferior.
                  Con 30 min entre 19:30 y 20:30 hay dos huecos (19:30 y 20:00).
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <div>
                  <Label htmlFor="urgent_opening_time">Apertura urgente</Label>
                  <Input
                    id="urgent_opening_time"
                    type="time"
                    value={schedule.urgent_opening_time}
                    onChange={(e) =>
                      setSchedule((prev) => ({ ...prev, urgent_opening_time: e.target.value }))
                    }
                    className="h-11 mt-1 w-[140px]"
                  />
                </div>
                <div>
                  <Label htmlFor="urgent_closing_time">Cierre urgente</Label>
                  <Input
                    id="urgent_closing_time"
                    type="time"
                    value={schedule.urgent_closing_time}
                    onChange={(e) =>
                      setSchedule((prev) => ({ ...prev, urgent_closing_time: e.target.value }))
                    }
                    className="h-11 mt-1 w-[140px]"
                  />
                </div>
                <div>
                  <Label htmlFor="urgent_appointment_duration">Duración (min)</Label>
                  <Input
                    id="urgent_appointment_duration"
                    type="number"
                    min="15"
                    step="15"
                    value={schedule.urgent_appointment_duration}
                    onChange={(e) =>
                      setSchedule((prev) => ({
                        ...prev,
                        urgent_appointment_duration: parseInt(e.target.value, 10) || 30,
                      }))
                    }
                    className="h-11 mt-1 w-32"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Día</TableHead>
                    <TableHead className="w-[72px] text-center">Activo</TableHead>
                    <TableHead>Apertura</TableHead>
                    <TableHead>Cierre</TableHead>
                    <TableHead>Inicio comida</TableHead>
                    <TableHead>Fin comida</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {DAYS_OF_WEEK.map((day) => {
                    const k = String(day.id);
                    const row = schedule.per_day[k];
                    const active = normalizeWorkingDays(schedule.working_days).includes(day.id);
                    return (
                      <TableRow key={day.id} className={!active ? 'opacity-60' : ''}>
                        <TableCell className="font-medium">{day.name}</TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={active}
                            onCheckedChange={() => handleDayToggle(day.id)}
                            data-testid={`checkbox-day-${day.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="time"
                            value={row.opening_time}
                            onChange={(e) => handlePerDayChange(day.id, 'opening_time', e.target.value)}
                            className="h-9 min-w-[120px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="time"
                            value={row.closing_time}
                            onChange={(e) => handlePerDayChange(day.id, 'closing_time', e.target.value)}
                            className="h-9 min-w-[120px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="time"
                            value={row.lunch_start}
                            onChange={(e) => handlePerDayChange(day.id, 'lunch_start', e.target.value)}
                            className="h-9 min-w-[120px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="time"
                            value={row.lunch_end}
                            onChange={(e) => handlePerDayChange(day.id, 'lunch_end', e.target.value)}
                            className="h-9 min-w-[120px]"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <Button
              onClick={handleSaveSchedule}
              className="w-full rounded-full gap-2 sm:w-auto sm:min-w-[240px]"
              disabled={saving}
              data-testid="save-schedule-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar Horarios'}
            </Button>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-primary" />
              Excepciones del Calendario
            </CardTitle>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-full gap-1" data-testid="add-exception-btn">
                  <Plus className="w-4 h-4" />
                  Agregar
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Agregar excepción de horario</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Fecha</Label>
                    <Calendar
                      mode="single"
                      selected={newException.date}
                      onSelect={(date) => {
                        if (!date) {
                          setNewException((prev) => ({ ...prev, date: null }));
                          return;
                        }
                        const ymd = datePickerToClinicYmd(date);
                        setNewException((prev) => ({
                          ...prev,
                          date: ymdToDatePickerDate(ymd),
                        }));
                      }}
                      fromDate={new Date()}
                      locale={es}
                      className="rounded-lg border mt-2"
                      data-testid="exception-calendar"
                    />
                  </div>
                  <div>
                    <Label htmlFor="exception_reason">Motivo</Label>
                    <Input
                      id="exception_reason"
                      value={newException.reason}
                      onChange={(e) => setNewException((prev) => ({ ...prev, reason: e.target.value }))}
                      placeholder="Ej: Día festivo, curso, mantenimiento"
                      className="h-11 mt-1"
                      data-testid="exception-reason-input"
                    />
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer rounded-lg border p-3">
                    <Checkbox
                      checked={newException.allDay}
                      onCheckedChange={(c) =>
                        setNewException((prev) => ({ ...prev, allDay: Boolean(c) }))
                      }
                      data-testid="exception-all-day"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Cerrar todo el día</span>
                      <span className="block text-muted-foreground text-xs mt-0.5">
                        Sin consultas en ningún horario de esa fecha
                      </span>
                    </span>
                  </label>
                  {!newException.allDay ? (
                    <div className="grid grid-cols-2 gap-3 rounded-lg border border-dashed p-3">
                      <div>
                        <Label htmlFor="exception_closed_start">Desde (sin servicio)</Label>
                        <Input
                          id="exception_closed_start"
                          type="time"
                          className="h-11 mt-1"
                          value={newException.closedStart}
                          onChange={(e) =>
                            setNewException((prev) => ({ ...prev, closedStart: e.target.value }))
                          }
                          data-testid="exception-closed-start"
                        />
                      </div>
                      <div>
                        <Label htmlFor="exception_closed_end">Hasta (sin servicio)</Label>
                        <Input
                          id="exception_closed_end"
                          type="time"
                          className="h-11 mt-1"
                          value={newException.closedEnd}
                          onChange={(e) =>
                            setNewException((prev) => ({ ...prev, closedEnd: e.target.value }))
                          }
                          data-testid="exception-closed-end"
                        />
                      </div>
                      <p className="col-span-2 text-xs text-muted-foreground">
                        Esas horas no aparecerán como disponibles en la web ni en WhatsApp.
                      </p>
                    </div>
                  ) : null}
                  <Button onClick={handleAddException} className="w-full rounded-full" data-testid="save-exception-btn">
                    Agregar Excepción
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {exceptions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarDays className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No hay excepciones configuradas</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Alcance</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exceptions.map((exception) => (
                    <TableRow key={exception.id} data-testid={`exception-row-${exception.id}`}>
                      <TableCell>
                        {formatAppointmentDateOnlyInClinic(exception.date, 'd MMM yyyy')}
                      </TableCell>
                      <TableCell className="text-sm">{formatExceptionScope(exception)}</TableCell>
                      <TableCell className="text-muted-foreground">{exception.reason}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-accent hover:text-accent"
                          onClick={() => handleDeleteException(exception.id)}
                          data-testid={`delete-exception-${exception.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
